import Big from 'big.js';
import classNames from 'classnames';
import { ChangeEvent, ReactNode, useCallback } from 'react';
import { BigRoundMode } from '@/modules/common/constants/big-round-mode.ts';
import { TextInput } from 'ui';
import { formatValidationError, getTextInputExample } from 'ui/utils';
import { RoundingType } from 'api';

interface CurrencyInputProps {
  name?: string;
  error?: string;
  label?: string | ReactNode;
  disabled?: boolean;
  placeholder?: string;
  value?: string | number;
  allowFractional?: boolean;
  onChange: (value: number | string) => void;
  decimalPoints?: number;
  roundingType?: RoundingType | undefined;
  className?: string;
  classNameCurrency?: string;
  currency?: string;
  withBorder?: boolean;
  errorOutside?: boolean;
}

const CurrencyInput = ({
  name,
  label,
  error,
  disabled,
  value,
  placeholder,
  allowFractional = true,
  onChange,
  decimalPoints = 2,
  roundingType,
  className,
  classNameCurrency,
  currency = '$',
  withBorder,
  errorOutside = false,
}: CurrencyInputProps) => {
  const step = 1 / 10 ** decimalPoints;
  const renderErrorOutside = errorOutside && !!error;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value || '';

      if (!value) {
        onChange(value);
        return;
      }

      //We remove all the characters, except for numbers, commas and points
      value = value.replace(/[^\d.,]/g, '');

      // Whole-unit fields must never silently round a user-entered decimal.
      // Ignore the change instead, keeping the previous controlled value.
      if (!allowFractional && /[.,]/.test(value)) {
        return;
      }

      //We allow only one point
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }

      //Comma processing
      let hasDot = value.includes('.');
      if (hasDot) {
        // If there is a point, the commas are just deleted
        value = value.replace(/,/g, '');
      } else {
        // if there is no point, but there is one comma, replace it with a point
        const commas = value.match(/,/g)?.length || 0;
        if (commas === 1) {
          value = value.replace(',', '.');
        } else {
          // several commas - we delete everything
          value = value.replace(/,/g, '');
        }
      }

      const regex = /^[0-9.,]*$/;

      if (!regex.test(value)) {
        return;
      }

      value = value.replaceAll(',', '.').replaceAll('..', '.');
      hasDot = value.includes('.');
      if (!value) {
        onChange(value);
        return;
      }
      if (value === '.') {
        return;
      }
      if (hasDot) {
        const frac = value.split('.')?.[1] || '';
        if (value.endsWith('.') || frac.length <= decimalPoints) {
          onChange(value); // keep as string while user types
          return;
        }
        return;
      }

      if (allowFractional) {
        onChange(value);
        return;
      }

      let num = new Big(value);
      num =
        roundingType === RoundingType.Up
          ? num.round(0, BigRoundMode.roundUp)
          : num.round(0, BigRoundMode.roundDown);
      onChange(Number(num.valueOf()));
    },
    [allowFractional, decimalPoints, onChange, roundingType],
  );

  const paddingLeft = currency
    ? `calc(${currency.length * 0.8}rem + ${withBorder ? '4px' : '0px'})`
    : undefined;

  return (
    <div className="w-full">
      <div className="relative w-full">
        {currency && (
          <span
            className={classNames(
              'absolute left-0 z-[1] text-[#616161]',
              {
                'bottom-[13px]':
                  !classNameCurrency?.includes('bottom-') && (!error || renderErrorOutside),
                'bottom-[38px]':
                  !classNameCurrency?.includes('bottom-') && !!error && !renderErrorOutside,
                'pl-[6px]': withBorder,
              },
              classNameCurrency,
            )}
          >
            {currency}
          </span>
        )}
        <TextInput
          label={label}
          name={name ?? 'money'}
          className={classNames(className)}
          style={{
            paddingLeft,
          }}
          // type="number"
          disabled={disabled}
          min={allowFractional ? step : 1}
          step={allowFractional ? step : 1}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={handleChange}
          withBorder={withBorder}
          error={error}
          hideErrorMessage={renderErrorOutside}
        />
      </div>
      {renderErrorOutside && (
        <p className="mt-1 text-sm text-[12px] text-error-red">
          {formatValidationError(
            error,
            getTextInputExample({ name: name ?? 'money', placeholder }),
          )}
        </p>
      )}
    </div>
  );
};

export default CurrencyInput;
